import { Box, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import {
  useSimulationSimulationJobGetAssetClassResultSummaryListQuery,
  useSimulationSimulationJobGetFiftyPercentileListQuery,
} from "@app/api/carApi.generated";
import { BaseDialog } from "@app/components/BaseDialog";
import {
  CarInfoCell,
  CarInfoHeader,
  CarInfoTable,
} from "@app/components/InfoTable";
import { Fragment } from "react";
import { formatPercentFactor, isOddEven } from "@app/utils";
import { CarPortfolioAssetTableData } from "./usePortfolioGroupVersionData";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";

interface TableRow {
  id: string;
  assetClassName: string;
  volatility: number;
  weight: number;
  weightedVolatility: number;
}

interface PortfolioGroupVolatilityDialogProps {
  dataId: string;
  moduleId: string;
  tableData: CarPortfolioAssetTableData;
  simulationJobId: string;

  onApply: () => void;
  onCancel: () => void;
}

export const PortfolioGroupVolatilityDialog = (
  props: PortfolioGroupVolatilityDialogProps,
) => {
  const data = useSimulationSimulationJobGetAssetClassResultSummaryListQuery({
    id: props.simulationJobId,
  });

  const items: TableRow[] = props.tableData.assetRows.level4
    .map((i) => {
      const weight =
        i.modules
          .find((md) => md.moduleId === props.moduleId)
          ?.values.find((v) => v.dataId === props.dataId)?.allocation ?? 0;

      const volatility =
        data.data?.find((d) => d.asset_class4 === i.id)
          ?.output_nominal_return_sigma ?? 0;
      const row: TableRow = {
        id: i.id,
        assetClassName: i.name,
        volatility,
        weight,
        weightedVolatility: volatility * weight,
      };
      return row;
    })
    .filter((i) => i.weight > 0);

  const band = props.tableData.bands.find((i) => i.moduleId === props.moduleId);
  const column = band?.columns.find((i) => i.dataId === props.dataId);

  return (
    <BaseDialog
      sx={{ width: "1200px", maxWidth: "100vw" }}
      onClose={props.onCancel}
    >
      <TablePlaceHolder isLoading={data.isLoading} rows={8}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <Typography variant="h2SSemiBold">
            {`Simulation: Volatility of ${column?.label}`}
            <CarTooltipBox
              sx={{ display: "inline-block", ml: 1, mb: -0.25 }}
              className={pendoClasses.editPortfolioVolatility}
            />
          </Typography>
          <Typography variant="par02SemiBold" sx={{ mt: 5, fontWeight: 700 }}>
            {band?.label}
          </Typography>
          <CarInfoTable
            sx={{ mt: 1, gridTemplateColumns: "1.5fr 1fr 1fr 1fr" }}
          >
            <CarInfoHeader text="" />
            <CarInfoHeader text="Volatility" />
            <CarInfoHeader text="Weight" />
            <CarInfoHeader text="Weighted Volatility" />
            {items.map((i, idx) => (
              <Fragment key={i.id}>
                <CarInfoCell
                  text={i.assetClassName}
                  justifyContent="start"
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.volatility, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.weight, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
                <CarInfoCell
                  text={formatPercentFactor(i.weightedVolatility, {
                    decimalPlaces: 1,
                  })}
                  isOdd={isOddEven(idx)}
                />
              </Fragment>
            ))}
          </CarInfoTable>
        </Box>
      </TablePlaceHolder>
    </BaseDialog>
  );
};
